/**
 * Bug-fix pipeline — the PURE core (docs/features/bug-fix-pipeline).
 *
 * The pipeline is three cooperating parts, and this module is the one place
 * they agree on what a bug's state means:
 *
 *   1. THE TICK — a scheduled local Claude session (TICK.md is its runbook).
 *      It reads the queue through scripts/bug-pipeline/queue.mts, writes fix
 *      recommendations, claims approved bugs, implements fixes on a worktree,
 *      pushes, live-verifies, and stamps every transition through
 *      updateBugReport (never raw KV — the list index must stay mirrored).
 *   2. THE NOTIFY CRON — app/api/cron/bug-pipeline-notify (every 15 min,
 *      server-side, where RESEND_API_KEY / AUTH_SECRET / CRON_SECRET live).
 *      It turns stamped state into email: the fix-recommendation email to the
 *      operator (with the signed Approve button), pipeline notices, and —
 *      via the existing /api/bug-reports/resolve route, which it calls with
 *      the house cron secret — the reporter resolution email. Every send is
 *      idempotent through an `…EmailedAt` stamp / pending flag on the row.
 *   3. THE OPERATOR — approves from the email (the existing signed-token
 *      /approve route) or the settings UI. Approval is the authorization for
 *      fix + push + resolve; nothing ships without it.
 *
 * Stage selection, the email-work scan, and the KV patches are pure functions
 * here so the tick, the cron, and the tests cannot disagree about what
 * happens next.
 */

import type { BugReport } from '@/lib/bug-reports'
// TYPE-ONLY on purpose: lib/bug-queues VALUE-imports this module
// (isAutoApprovedOnFiling), so a value import back would be a runtime cycle.
// The queue view carries the summaries; producing them (queueSummaries) stays
// the caller's job — see annotatePipelineQueue.
import type { QueueSummary } from '@/lib/bug-queues'
// Pure + client-safe, and deliberately NOT lib/bug-queues (a value import of that module
// here is a runtime cycle — see the QueueExecutionInput note).
import { resolveAreaOwner } from '@/lib/bug-pipeline-owners'
// The ONE list of shared surfaces. Read here and by the notify route, so the approval email and
// the approval RULE can never disagree about what counts as blast radius.
import { needsNamedApproval } from '@/lib/bug-pipeline-protected'

// ── Constants ────────────────────────────────────────────────────────────────

/** Assignee marker the tick claims bugs under. A bug in_progress under any
 *  OTHER assignee belongs to a human and the pipeline must not touch it. */
export const PIPELINE_ASSIGNEE = 'Claude (bug pipeline)'

/** How many times the pipeline may start a fix on one bug (first attempt +
 *  one retry after a reporter Reactivate or a dead tick). Beyond this the
 *  bug is blocked with a notice — a loop of failed fixes must page the
 *  operator, not burn tokens forever. */
export const MAX_PIPELINE_ATTEMPTS = 2

/** A claim older than this with no pushed fix means the tick that claimed it
 *  died (session killed, machine rebooted). The bug becomes claimable again. */
export const STALE_CLAIM_HOURS = 12

export const FIX_CONFIDENCES = ['high', 'medium', 'low'] as const
export type FixConfidence = (typeof FIX_CONFIDENCES)[number]

/**
 * The apps whose bugs this pipeline works (operator directive, Sep 5 2026: "the pipeline should
 * have the same support built for the website — if Oliver files a bug and I approve it, the
 * pipeline picks it up").
 *
 * ONE list, read by the stage machine, the email scan and the intake queues, so a project can
 * never be half-enabled: added to the bug vocabulary but not here, it is simply invisible to the
 * pipeline — reports still file, triage and render, nothing is worked. That is the safe default
 * and it is why `andrei-uber` stays out: those reports are history nobody works here.
 *
 * A project listed here needs a place for its fix to LAND. Portal fixes land in this repo, which
 * is the runner's own checkout; a `website` fix lands in fabiant314/green-labs-product-concepts
 * (see TICK-CLOUD.md, "Website bugs"), which the runner can only reach with a cross-repo token.
 */
export const PIPELINE_PROJECTS: readonly BugReport['project'][] = ['portal', 'website']

export function isPipelineProject(project: unknown): boolean {
  return (PIPELINE_PROJECTS as readonly string[]).includes(project as string)
}

/**
 * The VERDICT every recommendation must carry (operator directive, Sep 4
 * 2026: "reject bugs that do not make sense, or make a better proposal —
 * we're not just saying yes to everything").
 *
 *  - 'fix'              build it as reported
 *  - 'counter-proposal' build something DIFFERENT from what was asked; the
 *                       recommendation text IS the counter-proposal, and an
 *                       approval approves THAT (the fix stage follows the
 *                       stored recommendation)
 *  - 'wont-fix'         recommends declining — not a defect, by design,
 *                       conflicts with intent, duplicate, or would make the
 *                       product worse; the email's primary tap agrees
 *  - 'needs-decision'   a product choice must be made first; one question
 *
 * 'fix' is an OUTCOME, never a default: the queue CLI refuses a
 * recommendation without a verdict, so the tick has to record that it looked
 * for a reason to say no and found none.
 */
export const RECOMMENDATION_VERDICTS = ['fix', 'counter-proposal', 'wont-fix', 'needs-decision'] as const
export type RecommendationVerdict = (typeof RECOMMENDATION_VERDICTS)[number]

/** Operator-facing wording for each verdict — ONE definition for the email,
 *  the board and the bug page. */
export const VERDICT_LABELS: Record<RecommendationVerdict, string> = {
  fix: 'Fix as reported',
  'counter-proposal': 'Counter-proposal — a different fix than asked',
  'wont-fix': "Won't fix — the pipeline recommends declining",
  'needs-decision': 'Needs your decision first',
}

// ── Feature work items (operator directive, Sep 4 2026) ─────────────────────

/**
 * "The work should get filed as a bug/feature work and get picked up by the
 * cloud queue." A tracker item whose title starts with this tag is a
 * SPECIFICATION the pipeline builds in slices, one slice per run — not a
 * single-run fix. The tag lives in the title on purpose: it is visible in every
 * email subject, on the roster and on the board without any surface learning
 * a new field. Execution rules: TICK-CLOUD.md, "Feature work items".
 */
export const FEATURE_WORK_TAG = '[CLAUDE feature work]'

export function isFeatureWorkItem(bug: Pick<BugReport, 'title'>): boolean {
  return (bug.title ?? '').trimStart().startsWith(FEATURE_WORK_TAG)
}

/** What createBugReport needs — everything the form would have filled in. */
export type FeatureWorkItemInput = Omit<BugReport,
  'id' | 'createdAt' | 'updatedAt' | 'status' | 'priority' | 'assignee' | 'resolutionSummary'
  | 'closedAt' | 'approvalStatus' | 'approvedForFixAt' | 'approvedBy' | 'approvalSource'
  | 'approvalNote' | 'project'>

/**
 * A feature work item, filed by the CLI (an operator session today; the
 * settle stage's feature-work outcome tomorrow). The OPERATOR is the reporter:
 * they are the customer of the work and receive its resolution email. The
 * description IS the spec (plus where it lives in the repo and which reports
 * it consolidates), so the fixing tick needs nothing but the bug record.
 */
export function buildFeatureWorkItem(args: {
  title: string
  spec: string
  specPath?: string | null
  members?: string[]
  operatorEmail: string
  boardUrl: string
  severity?: BugReport['severity']
}): FeatureWorkItemInput {
  const title = args.title.trimStart().startsWith(FEATURE_WORK_TAG)
    ? args.title.trim()
    : `${FEATURE_WORK_TAG} ${args.title.trim()}`
  const members = (args.members ?? []).filter(Boolean)
  const footer = [
    args.specPath ? `Spec in the repo: ${args.specPath}` : null,
    members.length ? `Consolidates: ${members.join(', ')}` : null,
    `Filed through queue.mts file-work on behalf of ${args.operatorEmail}. Built by the bug-fix `
      + 'pipeline in slices (TICK-CLOUD.md, "Feature work items"); progress is the pipelineWorkLog on this item.',
  ].filter(Boolean).join('\n')
  return {
    title,
    description: `${args.spec.trim()}\n\n---\n${footer}`,
    expectedBehavior: 'Every acceptance criterion in the description is live, the full test gate is green '
      + 'after each slice, and the work log\'s last entry reads "NEXT: DONE".',
    stepsToReproduce: 'n/a — feature work; the description is the specification.',
    category: 'feature-request',
    severity: args.severity ?? 'medium',
    affectedArea: 'Bug Pipeline',
    environment: 'production',
    frequency: 'always',
    blockedWork: false,
    workaroundFound: false,
    workaroundDetails: '',
    currentUrl: args.boardUrl,
    currentRoute: '/settings/bugs/pipeline',
    reporterName: 'Bug pipeline (feature work)',
    reporterEmail: args.operatorEmail,
    browserInfo: 'n/a — filed by queue.mts',
    osInfo: 'n/a',
    deviceInfo: 'n/a',
    viewport: 'n/a',
    appVersion: 'n/a',
    reproductionContext: JSON.stringify({ filedBy: 'queue.mts file-work', specPath: args.specPath ?? null, members }),
    screenshotUrl: null,
    additionalScreenshotUrls: [],
    attachments: [],
  }
}

/**
 * Every pipeline field on BugReport, as ONE walked list. The BVT walks this
 * against the BugReport interface AND the updateBugReport whitelist so a
 * field can never be added here and silently dropped by the persistence
 * layer (see feedback: guards must walk, not enumerate twice).
 */
export const PIPELINE_UPDATE_FIELDS = [
  'fixRecommendation',
  'pipelinePlannedFiles',
  'fixRecommendationConfidence',
  'fixRecommendationVerdict',
  'fixRecommendationAt',
  'recommendationEmailedAt',
  'pipelineClaimedAt',
  'pipelineAttempts',
  'pipelineFixCommit',
  'pipelineFixPushedAt',
  'pipelineFixNotes',
  'resolveEmailPending',
  'resolveEmailedAt',
  'pipelineNotice',
  'pipelineNoticeEmailedAt',
  'pipelineNoticeAsksReporter',
  'pipelineQuestionEmailedAt',
  'pipelineBlockedReason',
  'pipelineOperatorGuidance',
  'pipelineOperatorGuidanceAt',
  'pipelineWorkLog',
  'pipelineQueueId',
  'consolidatedInto',
  'resolutionKind',
  'duplicateOf',
] as const
export type PipelineUpdateField = (typeof PIPELINE_UPDATE_FIELDS)[number]

/** The fields a pipeline patch may touch: the pipeline's own fields plus the
 *  triage fields its transitions legitimately move. */
export type PipelinePatch = Partial<Pick<BugReport,
  PipelineUpdateField | 'status' | 'assignee' | 'resolutionSummary'
  | 'approvalStatus' | 'approvedForFixAt' | 'approvedBy' | 'approvalSource'>>

/** What stage selection needs to see — satisfied by both the full row and
 *  the list-index shape (BugReportListItem spreads every pipeline field). */
export type PipelineBugView = Pick<BugReport,
  'project' | 'status' | 'approvalStatus' | 'assignee'
  | 'fixRecommendation' | 'fixRecommendationAt' | 'recommendationEmailedAt'
  | 'pipelineClaimedAt' | 'pipelineAttempts' | 'pipelineFixPushedAt'
  | 'resolveEmailPending' | 'pipelineNotice' | 'pipelineNoticeEmailedAt'
  | 'pipelineBlockedReason' | 'consolidatedInto'>

// ── The tick clock ───────────────────────────────────────────────────────────

/**
 * The cloud tick's cron minutes (UTC) — MUST match the `schedule.cron` in
 * .github/workflows/bug-pipeline-tick.yml. BVT-pinned against the workflow
 * file, so the board's countdown can never silently drift from the real
 * cadence. (This module stays CLIENT-SAFE — type-only imports, no env, no
 * KV — because the pipeline board bundles it for exactly this.)
 */
export const TICK_CRON_MINUTES = [13, 43] as const

/** Next scheduled tick strictly after `now`. Pure. GitHub queues the run at
 *  this minute; actual start can lag a few minutes under scheduler load. */
export function nextTickAt(now: Date): Date {
  const base = new Date(now)
  base.setUTCSeconds(0, 0)
  for (let add = 0; add <= 60; add++) {
    const cand = new Date(base.getTime() + add * 60_000)
    if ((TICK_CRON_MINUTES as readonly number[]).includes(cand.getUTCMinutes())
      && cand.getTime() > now.getTime()) {
      return cand
    }
  }
  return new Date(now.getTime() + 30 * 60_000) // unreachable — defensive only
}

// ── Auto-start policy (operator directive, Sept 2026) ───────────────────────

/**
 * "Blockers should start immediately on filing."
 *
 * A BLOCKER-severity portal bug is approved at the moment it is filed — no
 * waiting for the operator to tap Approve. That is a real transfer of
 * authority: anyone who can file a bug can now start a production change by
 * choosing that severity. It is bounded on purpose:
 *
 *  - ONLY `severity === 'blocker'` (the reporter's most deliberate choice) and
 *    ONLY portal bugs. High/medium/low still wait for a human.
 *  - The filing email says so in its subject and body, and carries a one-tap
 *    ⏸ HOLD link — the auto-start's stop button. The pipeline can be halted
 *    before, during, or after the first tick.
 *  - Everything downstream is unchanged: the full test gate, one commit per
 *    bug, live verification before resolve. Nothing skips a safety step
 *    because a bug started itself.
 */
export function isAutoApprovedOnFiling(
  bug: Pick<BugReport, 'severity' | 'project'>,
): boolean {
  // Deliberately PORTAL-only, narrower than PIPELINE_PROJECTS: self-approval is a transfer of
  // authority, and it is granted only where the fix path is proven end to end. A website blocker
  // therefore waits for the operator's tap like every other website report — the same rule of
  // thumb that kept this to one project when it was introduced.
  return bug.severity === 'blocker' && bug.project === 'portal'
}

/**
 * May this bug be approved WITHOUT a tap that names the file — by the blocker policy above, or by a
 * queue-level "Approve all"?
 *
 * No, once the plan touches a shared surface. A blanket approval is exactly how a LOW-severity bug
 * came to change the welcome page for everyone (BUG-mtuinf1y-p146): the operator's tap was real,
 * but it was a tap on a queue, not on "yes, change the home page". Those need naming.
 *
 * Undeclared files are treated as UNSAFE for a blanket approval too — "we did not say" must never be
 * the cheaper path than "we said, and it is shared".
 */
export function canBlanketApprove(bug: Pick<BugReport, 'pipelinePlannedFiles'>): boolean {
  const files = bug.pipelinePlannedFiles
  if (!files || files.length === 0) return false
  return !needsNamedApproval(files)
}

/** The approval a filed blocker gets. `approvalSource: 'auto_blocker'` keeps
 *  it distinguishable from a human tap forever — an audit must be able to ask
 *  "did a person approve this?" and get a straight answer. */
export function buildFilingAutoApprovalPatch(now: Date): PipelinePatch {
  return {
    approvalStatus: 'approved',
    approvedForFixAt: now.toISOString(),
    approvedBy: 'auto: blocker policy',
    approvalSource: 'auto_blocker',
  }
}

/** One tap from the filing email stops an auto-started blocker. Uses the same
 *  block mechanism the pipeline already respects everywhere. */
export function buildHoldPatch(): PipelinePatch {
  return {
    pipelineBlockedReason:
      'Held by the operator from the filing email — the blocker auto-start was cancelled. '
      + 'Release it (or answer with guidance) on the pipeline board to resume.',
  }
}

// ── Stage selection ──────────────────────────────────────────────────────────

export type PipelineStage =
  | 'recommend'       // pending approval, no recommendation yet → tick investigates + recommends
  | 'await-approval'  // recommendation written; the operator has not decided
  | 'fix'             // approved and unclaimed (or the claim went stale) → tick implements + pushes
  | 'verify'          // fix pushed; a later tick live-verifies, then requests resolution
  | 'resolving'       // resolution requested; the notify cron is finishing it
  | 'exhausted'       // approved but the attempt cap is spent → tick blocks + notices the operator
  | 'waiting'         // an approved member of a fix-in-order plan whose turn has not come (item D),
                    //   or a report folded into a feature-work item awaiting its decision (item C)
  | 'blocked'         // pipelineBlockedReason set — a human must clear it (queue.mts unblock)
  | 'done'            // fixed / verified / closed
  | 'skip'            // not the pipeline's to touch

/**
 * Decide what (if anything) the pipeline should do with one bug.
 *
 * Deliberately conservative: anything a human has touched — a non-pipeline
 * assignee, a manual 'blocked' status, a rejected approval — is 'skip'.
 * The pipeline only ever works portal bugs; AndreiUber reports concern a
 * different app tree and stay with the operator.
 */
export function bugPipelineStage(bug: PipelineBugView, now: Date): PipelineStage {
  if (!isPipelineProject(bug.project)) return 'skip'
  if (bug.pipelineBlockedReason) return 'blocked'
  if (bug.status === 'fixed' || bug.status === 'verified' || bug.status === 'closed') return 'done'
  if (bug.status === 'blocked') return 'skip'
  if (bug.resolveEmailPending) return 'resolving'
  // Item C: folded into a [CLAUDE feature work] item and awaiting the operator's decision on
  // THAT item. Not this tick's to recommend or fix — the decision closes it as Duplicate Work
  // or releases it. `waiting` rather than `skip` on purpose: it is real work with a turn coming,
  // and the board's waiting lane prints the same reason (annotatePipelineQueue).
  if (consolidationHold(bug)) return 'waiting'

  if (bug.status === 'in_progress') {
    if ((bug.assignee ?? '') !== PIPELINE_ASSIGNEE) return 'skip'
    if (bug.pipelineFixPushedAt) return 'verify'
    const claimedAt = bug.pipelineClaimedAt ? Date.parse(bug.pipelineClaimedAt) : NaN
    const stale = !Number.isFinite(claimedAt)
      || now.getTime() - claimedAt > STALE_CLAIM_HOURS * 3_600_000
    if (!stale) return 'skip' // a live tick is on it right now
    return (bug.pipelineAttempts ?? 0) >= MAX_PIPELINE_ATTEMPTS ? 'exhausted' : 'fix'
  }

  // status 'new' | 'triaged'
  if (bug.approvalStatus === 'approved') {
    return (bug.pipelineAttempts ?? 0) >= MAX_PIPELINE_ATTEMPTS ? 'exhausted' : 'fix'
  }
  if (bug.approvalStatus === 'rejected') return 'skip'
  return bug.fixRecommendationAt ? 'await-approval' : 'recommend'
}

// ── Email-work scan (the notify cron's worklist) ─────────────────────────────

export interface PipelineEmailWork {
  /** Recommendation written but never emailed → operator recommendation email. */
  recommendation: string[]
  /** Resolution requested by the tick → finish via /api/bug-reports/resolve. */
  resolution: string[]
  /** Notice text stamped but never emailed → operator notice email. */
  notice: string[]
  /** Recommendation emails HELD because the bug's intake queue speaks for it.
   *  Two windows, both bounded:
   *   - OPEN (work item A): the reporter may still be filing sibling reports,
   *     so the operator hears once, when the queue settles. The cron settles
   *     due queues BEFORE this scan, so a settled queue's members flow out in
   *     the very same pass.
   *   - PLANNED and not yet emailed (work item B): the queue's ONE plan email
   *     is about to carry every member, so a per-bug email would be the exact
   *     duplicate the plan exists to replace. That email stamps each member's
   *     recommendationEmailedAt as it sends, so the hold ends the moment the
   *     operator has actually been told — a planned queue that never sends
   *     retries next pass rather than holding mail forever.
   *
   *  ONE case holds permanently, and it holds nothing real: a plan whose members
   *  the operator ALREADY decided on the board is never mailed at all
   *  (queueEmailInbox, BUG-mtosjhhu-9hza), so it stays planned-and-unsent. Every
   *  member it holds is approved or rejected, and the per-bug email it is holding
   *  is the "Approve for Fixing" ask — which those members have already answered.
   *  The reaper retires the queue once they finish, so it is bounded too. */
  suppressed: string[]
}

/**
 * The slice of an IntakeQueue this scan reads. STRUCTURAL on purpose: a value
 * import of lib/bug-queues here would be a runtime cycle (bug-queues imports
 * isAutoApprovedOnFiling from this module), and the scan needs nothing but
 * openness + membership.
 */
export interface EmailScanQueue {
  status: string
  members: ReadonlyArray<{ bugId: string }>
  /** Item B: when the queue's ONE plan email went out. A PLANNED queue that has not sent it
   *  yet holds its members' per-bug recommendation emails — that one email replaces them. */
  emailedAt?: string | null
}

export function selectPipelineEmailWork(
  items: Array<Pick<BugReport,
    'id' | 'project' | 'fixRecommendation' | 'recommendationEmailedAt'
    | 'resolveEmailPending' | 'pipelineNotice' | 'pipelineNoticeEmailedAt'
    | 'consolidatedInto'>>,
  queues?: readonly EmailScanQueue[],
): PipelineEmailWork {
  // ONLY the recommendation email is queue-suppressed. A notice is the
  // pipeline asking a human for help and a resolution is a finished fix —
  // holding either behind a quiet window would strand real work. No queues
  // (caller has none, or the queue doc was unreadable) → nothing is held:
  // fail-open is the safe direction for mail.
  //
  // Two holds, one set: a queue still OPEN (item A — the reporter may still be
  // typing) and a queue PLANNED whose one plan email has not gone out yet
  // (item B — that email carries every member, so a per-bug copy is the
  // duplicate the plan replaces). Once the plan email sends it stamps each
  // member, so this hold cannot outlive the send.
  const held = new Set<string>()
  for (const q of queues ?? []) {
    const holds = q.status === 'open' || (q.status === 'planned' && !q.emailedAt)
    if (holds) for (const m of q.members) held.add(m.bugId)
  }
  const recommendation: string[] = []
  const resolution: string[] = []
  const notice: string[] = []
  const suppressed: string[] = []
  for (const b of items) {
    // Recommendation + notice emails are operator mail about work this pipeline does, and the
    // tick only writes them on the projects it works — filter defensively anyway. (Both
    // templates brand by project, so a website recommendation reads as one.)
    const worked = isPipelineProject(b.project)
    // Item C: a report folded into a work item is spoken for by that item's ONE approval email.
    // Its own recommendation mail is held for as long as the stamp stands — including after the
    // item is approved and the report closes as Duplicate Work (the reporter has already been
    // told, once). A DECLINED consolidation clears the stamp, and the mail flows again.
    const consolidated = !!(b.consolidatedInto ?? '').trim()
    if (worked && b.fixRecommendation && !b.recommendationEmailedAt) {
      ;(held.has(b.id) || consolidated ? suppressed : recommendation).push(b.id)
    }
    if (b.resolveEmailPending) resolution.push(b.id) // the resolve route brands by project itself
    if (worked && b.pipelineNotice && !b.pipelineNoticeEmailedAt) notice.push(b.id)
  }
  return { recommendation, resolution, notice, suppressed }
}

// ── Transition patches (what queue.mts writes via updateBugReport) ───────────

export function buildRecommendPatch(args: {
  recommendation: string
  confidence: FixConfidence
  /** Required — see RECOMMENDATION_VERDICTS: 'fix' must be chosen, never assumed. */
  verdict: RecommendationVerdict
  /**
   * The repo-relative files this fix intends to change — the BLAST RADIUS.
   *
   * Declared at recommendation time so the approval email can show it. BUG-mtuinf1y-p146 was
   * approved from an email that carried the plan in prose and nothing about surfaces, and the
   * fix put a new first tile on the company's home page. The operator approved a paragraph;
   * they could not have approved the blast radius, because it was not there to read.
   */
  plannedFiles?: readonly string[]
  now: Date
}): PipelinePatch {
  return {
    fixRecommendation: args.recommendation,
    pipelinePlannedFiles: args.plannedFiles ? [...args.plannedFiles] : undefined,
    fixRecommendationConfidence: args.confidence,
    fixRecommendationVerdict: args.verdict,
    fixRecommendationAt: args.now.toISOString(),
    // A fresh recommendation supersedes any previously-sent one, so the
    // notify cron emails again — re-recommending is always deliberate
    // (a second attempt, or the operator asked for a better plan).
    recommendationEmailedAt: null,
  }
}

export function buildClaimPatch(args: { now: Date; priorAttempts: number }): PipelinePatch {
  return {
    status: 'in_progress',
    assignee: PIPELINE_ASSIGNEE,
    pipelineClaimedAt: args.now.toISOString(),
    pipelineAttempts: args.priorAttempts + 1,
  }
}

/**
 * The claim decision, made against the row as it is AT THE MOMENT OF WRITING.
 *
 * The claim is the pipeline's only mutual exclusion, and until Sep 10 2026 it was a
 * read-then-write: `queue.mts claim` read the row, checked the stage was `fix`, then wrote the
 * claim patch with no atomicity between the two. Two runs looking at the same unclaimed bug
 * would both see `fix` and both claim it. The tick workflow's single global concurrency group
 * is what made that impossible — one run at a time, which is also what caps the pipeline at one
 * bug per run.
 *
 * Pure so the rule is testable without KV: `updateBugReportGuarded` re-runs this against the
 * freshly-read row on every compare-and-set attempt, so the loser of a race sees the winner's
 * row and declines. `priorAttempts` is read HERE, off that fresh row — an attempt count read
 * before a racing write undercounts, and the attempt budget is what stops a bug retrying for ever.
 */
export function planBugClaim(
  fresh: PipelineBugView & { pipelineAttempts?: number | null },
  now: Date,
): { patch: PipelinePatch } | { refuse: string } {
  const stage = bugPipelineStage(fresh, now)
  if (stage !== 'fix') {
    return { refuse: `another run got there first — the stage is now '${stage}', not 'fix'` }
  }
  return { patch: buildClaimPatch({ now, priorAttempts: fresh.pipelineAttempts ?? 0 }) }
}

/**
 * A fix is on main. `notes` is the fix's technical account — the commit message, composed by
 * `lib/bug-fix-notes.ts` (and already merged with any notes an earlier fix of this bug left). It
 * is stored ON the bug because the operator reads the bug page, not `git log` (Oct 3 2026: "You
 * should absolutely update the bug with the comments you have when resolving it"). Omitted or
 * empty leaves whatever is stored alone — a commit that could not be read must never erase the
 * account of an earlier one.
 */
export function buildFixPushedPatch(args: { commit: string; now: Date; notes?: string | null }): PipelinePatch {
  const notes = (args.notes ?? '').trim()
  return {
    pipelineFixCommit: args.commit,
    pipelineFixPushedAt: args.now.toISOString(),
    ...(notes ? { pipelineFixNotes: notes } : {}),
  }
}

/**
 * Fix notes ALONE — for a fix made outside the pipeline (an operator session working a bug by
 * id). Deliberately touches no stage field: `pipelineFixCommit` / `pipelineFixPushedAt` are what
 * the verify stage keys off, and a bug the pipeline never claimed must not start reading as its
 * own pushed work.
 */
export function buildFixNotesPatch(args: { notes: string }): PipelinePatch {
  return { pipelineFixNotes: args.notes.trim() }
}

/**
 * Request resolution. Deliberately does NOT set status='fixed' — the notify
 * cron finishes this through POST /api/bug-reports/resolve (with the house
 * cron secret), which is the ONE place that flips the status and sends the
 * reporter email with the standing operator CCs. The summary is stored here
 * because the route falls back to `bug.resolutionSummary` when the caller
 * passes none.
 */
export function buildResolvePatch(
  args: { summary: string; verificationNote?: string | null },
): PipelinePatch {
  const patch: PipelinePatch = {
    resolutionSummary: args.summary,
    resolveEmailPending: true,
  }
  /**
   * The website resolve wall's escape hatch, recorded (lib/bug-pipeline-website.ts
   * `websiteResolveGate`). A tick may resolve a page the classifier cannot see — a wall with no
   * door becomes a reason to stop using the CLI — but never SILENTLY: the reason rides an
   * operator-only notice, so the one act that can tell a reporter something untrue is the one
   * the operator hears about. It is deliberately not in `resolutionSummary`: that text is the
   * reporter's email, and pipeline bookkeeping does not belong in it.
   */
  if (args.verificationNote) {
    patch.pipelineNotice = args.verificationNote
    patch.pipelineNoticeEmailedAt = null
    patch.pipelineNoticeAsksReporter = false
  }
  return patch
}

export function buildBlockPatch(
  args: { reason: string; notify?: boolean; operatorOnly?: boolean },
): PipelinePatch {
  const patch: PipelinePatch = { pipelineBlockedReason: args.reason }
  if (args.notify !== false) {
    patch.pipelineNotice = args.reason
    patch.pipelineNoticeEmailedAt = null
    /**
     * A BLOCK means the pipeline stopped and needs an answer — so the person who REPORTED the
     * bug is told by default (operator directive, Sep 5 2026: "make sure Oliver gets the ping on
     * 'we need your help, answer the question in this bug'").
     *
     * Default-on, opt-out, deliberately: the failure that matters is a reporter never learning
     * their bug is waiting on them, which looks exactly like a pipeline that went quiet. An
     * unnecessary ping is a mild annoyance and the operator is on every one of these anyway.
     * `operatorOnly` is for questions a reporter genuinely cannot answer (sequencing, product
     * strategy, anything about the pipeline itself).
     */
    patch.pipelineNoticeAsksReporter = args.operatorOnly !== true
  }
  return patch
}

/** An informational notice — nothing is waiting on anyone — so it stays operator-only unless the
 *  tick explicitly asks the reporter (`askReporter`). The blocking case is buildBlockPatch. */
export function buildNoticePatch(args: { notice: string; askReporter?: boolean }): PipelinePatch {
  return {
    pipelineNotice: args.notice,
    pipelineNoticeEmailedAt: null,
    pipelineNoticeAsksReporter: args.askReporter === true,
  }
}

export function buildUnblockPatch(): PipelinePatch {
  return { pipelineBlockedReason: null }
}

/**
 * Won't Fix — the operator's exit door (directive, Sep 1 2026: "comment from
 * Claude makes me rethink this and this should be a Won't Fix"). Closes the
 * bug WITHOUT the fixed/resolve path — deliberately no "your bug was fixed"
 * reporter email, because nothing was fixed. Approval flips to rejected so no
 * stage ever picks the bug up again, the reason lands where the roster shows
 * it (resolutionSummary), and any pending pipeline question is silenced.
 */
export function buildWontFixPatch(args: { reason: string }): PipelinePatch {
  return {
    status: 'closed',
    approvalStatus: 'rejected',
    resolutionSummary: args.reason,
    pipelineBlockedReason: null,
    pipelineNotice: null,
    pipelineNoticeEmailedAt: null,
  }
}

/**
 * The operator's one tap from a recommendation email: decline the bug. The
 * SAME exit as the board's Won't Fix (buildWontFixPatch — closed, rejected,
 * question silenced, no reporter "fixed" email because nothing was fixed).
 * The reason records where the decision came from and the first lines of the
 * pipeline's assessment, so the closed bug explains itself without the email.
 */
export function buildDeclinedFromEmailPatch(args: { recommendation?: string | null }): PipelinePatch {
  const head = (args.recommendation ?? '').trim().split('\n').slice(0, 4).join('\n').slice(0, 600)
  return buildWontFixPatch({
    reason: "Won't fix — declined by the operator from the recommendation email."
      + (head ? `\n\nPipeline assessment:\n${head}` : ''),
  })
}

/**
 * Operator guidance — the answer channel (operator directive, Aug 31 2026:
 * "if during development of the fix, Claude needs my help, I need a mechanism
 * to provide that"). When the pipeline blocks a bug with a question, the
 * operator types an answer on the pipeline board; this patch stores it,
 * LIFTS the block, and REFUNDS the attempts (human intervention resets the
 * cap) so the next tick retries the fix with the guidance in hand. The tick
 * treats `pipelineOperatorGuidance` as the operator's direct instruction.
 */
export function buildGuidancePatch(args: { guidance: string; now: Date }): PipelinePatch {
  return {
    pipelineOperatorGuidance: args.guidance,
    pipelineOperatorGuidanceAt: args.now.toISOString(),
    pipelineBlockedReason: null,
    pipelineAttempts: 0,
  }
}

/**
 * Return a claimed-but-unpushed bug to the approved queue — an interrupted or
 * abandoned tick (runner killed, operator stand-down). The attempt is REFUNDED:
 * it produced no push, and the attempt cap exists to stop failed-FIX loops,
 * not interrupted runs. A bug with a pushed fix must never be unclaimed
 * (queue.mts refuses) — that state belongs to the verify stage.
 */
export function buildUnclaimPatch(args: { priorAttempts: number }): PipelinePatch {
  return {
    status: 'new',
    assignee: null,
    pipelineClaimedAt: null,
    pipelineAttempts: Math.max(0, args.priorAttempts - 1),
  }
}

/**
 * Feature work: one slice shipped and verified — return the item to the
 * approved queue so the next run continues it. The claim and the push stamps
 * are cleared (the next slice is a fresh fix cycle), the attempt is REFUNDED
 * (the cap counts failed attempts at ONE slice, never slices), and the entry
 * is appended to the work log — the item's cursor. Entries end with
 * "NEXT: <the next slice>" or "NEXT: DONE" (queue.mts refuses otherwise).
 */
export function buildSliceDonePatch(args: { entry: string; priorLog?: string | null; now: Date }): PipelinePatch {
  const prior = (args.priorLog ?? '').trim()
  const stamp = args.now.toISOString().slice(0, 16).replace('T', ' ')
  const entry = `[${stamp}] ${args.entry.trim()}`
  return {
    status: 'new',
    assignee: null,
    pipelineClaimedAt: null,
    pipelineFixPushedAt: null,
    pipelineFixCommit: null,
    pipelineAttempts: 0,
    pipelineWorkLog: prior ? `${prior}\n\n${entry}` : entry,
  }
}

// ── Feature-work consolidation (work item C) ─────────────────────────────────
//
// When a settled queue's plan says `feature-work`, the pipeline files ONE
// [CLAUDE feature work] item carrying the synthesized prompt, and the member
// reports are folded into it: each gets `consolidatedInto: <itemId>` and STAYS
// `new`. Nothing is closed until the operator approves the ITEM — approving it
// closes every member as Duplicate Work and tells each reporter ONCE; declining
// it clears the stamps and the reports return to individual handling.
//
// Everything here is pure. The route/CLI halves (filing, the closures, the
// consolidation email, the release) are later slices and call these.

/** How a CLOSED report ended when it was not the ordinary fixed path. One word today —
 *  a report closed as Duplicate Work of the item named in `duplicateOf`. */
export const RESOLUTION_KINDS = ['duplicate'] as const
export type ResolutionKind = (typeof RESOLUTION_KINDS)[number]

/** What the hold reads off a row: the stamp plus the two fields that say the report is still
 *  awaiting a decision. */
export type ConsolidationHoldView = Pick<BugReport, 'consolidatedInto' | 'status' | 'approvalStatus'>

/**
 * The operator-facing reason a consolidated report may not be worked on its own — null ⇔ not
 * held. This is the ONE definition of "spoken for by a work item": the stage machine returns
 * `waiting` on it, the board prints it, and the email scan holds the report's per-bug
 * recommendation mail behind it.
 *
 * The stamp alone is not enough. It holds only while the report is still awaiting a decision
 * (status new/triaged, approval pending), so an individually APPROVED member — the operator
 * deliberately pulling one report out of the consolidation — is worked normally, and a member a
 * run is already holding keeps its claim. A stamp can never freeze work a person authorized.
 */
export function consolidationHold(bug: ConsolidationHoldView): string | null {
  const item = (bug.consolidatedInto ?? '').trim()
  if (!item) return null
  if (bug.status !== 'new' && bug.status !== 'triaged') return null
  if (bug.approvalStatus !== 'pending') return null
  return `Consolidated into ${item} — the operator decides on that work item. Approving it closes `
    + 'this report as Duplicate Work; declining releases it back here.'
}

/** Fold one report into a filed work item. It stays `new` on purpose: the filing is analysis,
 *  the closure waits for the operator's tap on the item. */
export function buildConsolidationStampPatch(args: { workItemId: string }): PipelinePatch {
  return { consolidatedInto: args.workItemId }
}

/**
 * Close one member as Duplicate Work of the approved item.
 *
 * Deliberately NOT the resolve path: `resolveEmailPending` stays unset, so no "your bug was
 * fixed" email goes out — nothing was fixed yet. The reporter hears once, through the
 * consolidation email the route sends (one per reporter, never one per report). `approvalStatus`
 * is left alone: the report was consolidated, not rejected, and `status: 'closed'` already makes
 * the stage machine read `done`. Any pending pipeline question is silenced, exactly as the
 * won't-fix exit does — a closed report must not keep asking.
 */
export function buildDuplicateClosurePatch(args: {
  workItemId: string
  workItemTitle?: string | null
}): PipelinePatch {
  const title = (args.workItemTitle ?? '').trim()
  return {
    status: 'closed',
    resolutionKind: 'duplicate',
    duplicateOf: args.workItemId,
    consolidatedInto: args.workItemId,
    resolutionSummary: title
      ? `Consolidated into ${title} (${args.workItemId})`
      : `Consolidated into ${args.workItemId}`,
    pipelineBlockedReason: null,
    pipelineNotice: null,
    pipelineNoticeEmailedAt: null,
  }
}

/** The operator declined the consolidation: erase every trace of it from the report, which
 *  returns to individual handling exactly as it was. Never touches `status` — releasing is not
 *  reopening, and a report that somehow closed stays closed (see planConsolidationRelease). */
export function buildConsolidationReleasePatch(): PipelinePatch {
  return { consolidatedInto: null, duplicateOf: null, resolutionKind: null }
}

/**
 * What a surface SHOWS about a report's place in a consolidation — the ONE definition, so the
 * roster, the pipeline board, the bug page and the reporter's own page can never disagree about
 * where a report went. Null means there is nothing to show.
 *
 * Two states, because both are real and they mean opposite things to whoever is reading:
 *  - 'duplicate' — the operator approved the item and this report is CLOSED as Duplicate Work
 *    of it (the chip the roster and the board print).
 *  - 'pending' — the report is folded into a filed item and is waiting for the operator's
 *    decision on that item; `consolidationHold` is the matching reason it is not being worked.
 *
 * The closed form wins when both stamps are present, because that is what actually happened to
 * the report. A row marked `duplicate` whose `duplicateOf` was somehow never written falls back
 * to the fold stamp rather than rendering a chip that points nowhere: a link to nothing is worse
 * than no chip, and the fold stamp names the same item.
 */
export type ConsolidationLinkView = Pick<BugReport, 'consolidatedInto' | 'resolutionKind' | 'duplicateOf'>

export interface ConsolidationLink {
  /** The work item — linked on the admin surfaces, named (never linked) on the reporter's. */
  workItemId: string
  kind: ResolutionKind | 'pending'
  /** The chip's words, e.g. "Duplicate \u2192 BUG-x". Rendered verbatim; no surface re-words it. */
  label: string
}

export function consolidationLink(bug: ConsolidationLinkView): ConsolidationLink | null {
  const folded = (bug.consolidatedInto ?? '').trim()
  const duplicated = (bug.duplicateOf ?? '').trim()
  if (bug.resolutionKind === 'duplicate') {
    const id = duplicated || folded
    return id ? { workItemId: id, kind: 'duplicate', label: `Duplicate \u2192 ${id}` } : null
  }
  return folded
    ? { workItemId: folded, kind: 'pending', label: `Consolidated \u2192 ${folded}` }
    : null
}

/** What the two consolidation decisions read off each row. */
export type ConsolidationRowView = Pick<BugReport, 'id' | 'consolidatedInto' | 'status' | 'approvalStatus'>

export interface ConsolidationDecision {
  /** Members to act on, in the caller's order (the queue's build order). */
  act: string[]
  /** Members deliberately left alone, each with the reason — the result page prints these. */
  skipped: Array<{ bugId: string; reason: string }>
}

/** The rows stamped with THIS item. A row stamped with a different item (or none) is not part of
 *  this consolidation at all and is never mentioned in the decision. */
const consolidatedMembers = <T extends { consolidatedInto?: string | null }>(
  rows: readonly T[],
  workItemId: string,
): T[] => rows.filter((r) => (r.consolidatedInto ?? '').trim() === workItemId.trim())

/**
 * Which members the item's APPROVAL closes as Duplicate Work.
 *
 * Every skip is a report whose state says a person already decided something about it, and
 * closing it would discard that: one approved on its own (the operator pulled it out of the
 * consolidation), one a run or a human is holding, one already finished or closed. Silence is
 * the failure mode this prevents — the caller prints the skips, so a member that did NOT close
 * is visible rather than assumed.
 */
export function planConsolidationClosure(
  rows: readonly ConsolidationRowView[],
  workItemId: string,
): ConsolidationDecision {
  const act: string[] = []
  const skipped: Array<{ bugId: string; reason: string }> = []
  for (const r of consolidatedMembers(rows, workItemId)) {
    if (r.status === 'closed') { skipped.push({ bugId: r.id, reason: 'already closed' }); continue }
    if (r.status === 'fixed' || r.status === 'verified') {
      skipped.push({ bugId: r.id, reason: `already resolved on its own (${r.status})` }); continue
    }
    if (r.status === 'in_progress') {
      skipped.push({ bugId: r.id, reason: 'in progress — closing it would discard work under way' }); continue
    }
    if (r.approvalStatus === 'approved') {
      skipped.push({ bugId: r.id, reason: 'approved on its own — left for individual handling' }); continue
    }
    act.push(r.id)
  }
  return { act, skipped }
}

/**
 * Which members a DECLINED consolidation releases back to individual handling.
 *
 * A member that is somehow already closed is skipped rather than reopened: releasing is undoing
 * a stamp, never resurrecting a decided report — the safe direction, since a wrong closure is
 * fixed by a person in one click and a wrong resurrection re-opens work nobody asked for.
 */
export function planConsolidationRelease(
  rows: readonly ConsolidationRowView[],
  workItemId: string,
): ConsolidationDecision {
  const act: string[] = []
  const skipped: Array<{ bugId: string; reason: string }> = []
  for (const r of consolidatedMembers(rows, workItemId)) {
    if (r.status === 'closed') {
      skipped.push({ bugId: r.id, reason: 'already closed — reopen it by hand if that was wrong' }); continue
    }
    act.push(r.id)
  }
  return { act, skipped }
}

/** What the consolidation email needs off each closed member. */
export type ConsolidationReportView = Pick<BugReport, 'id' | 'title' | 'reporterName' | 'reporterEmail'>

export interface ConsolidationRecipient {
  /** Canonical (trimmed, lowercased) address — one mailbox is ONE recipient however it was typed. */
  email: string
  /** Display name from the reporter's own reports; the address local-part when they gave none. */
  name: string
  /** Their reports in this consolidation, by id. */
  reports: Array<{ id: string; title: string }>
}

/**
 * Group the closed members into ONE entry per reporter — acceptance criterion 2: a consolidation
 * sends each person a single email listing their reports, never one mail per report (the failure
 * this replaces is eight "your bug was fixed" mails for one redesign).
 *
 * Order-independent by construction: recipients sort by address and their reports by id, so a
 * shuffled input produces a byte-identical result. Case and whitespace fold into one mailbox —
 * `Oliver@…` and `oliver@…` are one person, and treating them as two is exactly the duplicate
 * this function exists to prevent. A row with no address yields no recipient (there is nobody to
 * mail) rather than a send to an empty string.
 */
export function consolidationRecipients(
  rows: readonly ConsolidationReportView[],
): ConsolidationRecipient[] {
  const byEmail = new Map<string, { names: Map<string, string>; reports: Map<string, string> }>()
  for (const r of rows) {
    const email = (r.reporterEmail ?? '').trim().toLowerCase()
    if (!email || !r.id) continue
    let entry = byEmail.get(email)
    if (!entry) { entry = { names: new Map(), reports: new Map() }; byEmail.set(email, entry) }
    entry.reports.set(r.id, (r.title ?? '').trim())
    const name = (r.reporterName ?? '').trim()
    if (name) entry.names.set(r.id, name)
  }
  return [...byEmail.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([email, entry]) => {
      const reports = [...entry.reports.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([id, title]) => ({ id, title }))
      const named = [...entry.names.entries()].sort((a, b) => a[0].localeCompare(b[0]))[0]
      return { email, name: named ? named[1] : email.split('@')[0], reports }
    })
}

// ── Queue execution (work item D): an approved plan is ONE piece of work ─────

/**
 * The slice of a planned queue the executor reads. STRUCTURAL on purpose —
 * `QueueSummary` (lib/bug-queues) satisfies it — because a VALUE import of
 * that module here would be a runtime cycle (it value-imports
 * isAutoApprovedOnFiling from this one). Same reason as EmailScanQueue above.
 */
export interface QueueExecutionInput {
  id: string
  title: string
  status: string
  outcome: string | null
  /** Queue ids that must finish before this one may start. */
  after: readonly string[]
  /** The members in the plan, each with its build step and any per-member verdict.
   *  `needs` (optional) are the STEP NUMBERS this member waits on — see
   *  `effectiveNeeds` for why a plan that declares none stays strictly ordered. */
  planMembers: ReadonlyArray<{
    bugId: string
    step: number | null
    verdict: string | null
    needs?: readonly number[] | null
  }>
}

/**
 * WHY a member may not be worked yet. The board lanes split on this and the
 * notify cron emails only on `needs-answer`, so the three cases must stay
 * distinguishable rather than collapsing into one "waiting" string — a plan
 * waiting on a person and a plan waiting on its own earlier step want
 * completely different things from the reader.
 */
export type WaitingKind =
  | 'other-plan'    // a whole plan listed in `after` has not finished
  | 'earlier-step'  // this plan's own prerequisite steps are unfinished
  | 'needs-answer'  // the plan asked a question about THIS member and nobody has answered

export interface WaitingReason {
  kind: WaitingKind
  /** The one-line, operator-facing sentence. */
  text: string
}

/** Only a fix-in-order plan is EXECUTED as a unit: feature-work records only,
 *  wont-fix builds nothing, needs-decision is waiting on a person. */
export const QUEUE_EXECUTION_OUTCOME = 'fix-in-order'

/** A plan is under execution once the operator has approved it (`approved`)
 *  and while a run holds a member (`working`). Earlier statuses have not been
 *  authorized; `done` / `released` are over. */
export const QUEUE_EXECUTING_STATUSES: readonly string[] = ['approved', 'working']

/** Member stages that no longer hold their step: the fix is out the door
 *  (`done`) or its resolution is with the cron (`resolving`). Everything else
 *  — including `blocked` — HOLDS the step, on purpose: "fix in order" means a
 *  later step was planned on top of an earlier one, so silently stepping over
 *  a stalled member would build step 2 on a step 1 that never happened. The
 *  stall is visible (the board prints the reason and names the member), never
 *  silent. */
export const QUEUE_FINISHED_STAGES: readonly PipelineStage[] = ['done', 'resolving']

export function isExecutableQueue(q: Pick<QueueExecutionInput, 'status' | 'outcome'>): boolean {
  return q.outcome === QUEUE_EXECUTION_OUTCOME && QUEUE_EXECUTING_STATUSES.includes(q.status)
}

/** Where an unnumbered member sorts: last, behind every numbered step — the
 *  SAME rule as orderedPlanMembers, so the executor runs the order the
 *  operator approved. */
const UNSTEPPED = Number.MAX_SAFE_INTEGER

const stepValueOf = (m: { step: number | null }): number =>
  typeof m.step === 'number' && Number.isFinite(m.step) ? m.step : UNSTEPPED

/**
 * A plan is a DEPENDENCY GRAPH as soon as any member declares `needs`;
 * otherwise it is the strictly-ordered ladder it has always been.
 *
 * The default matters more than the feature. A legacy plan carries step
 * numbers and nothing else, and those numbers were approved as an ORDER — the
 * Sep 9 plan says step 4 is "easier after step 3, which is where the inline
 * age-gate bootstrap can move to an external file". Reading a bare ladder as
 * "independent unless stated" would start building on top of work that has not
 * happened, in a repo that deploys to a live public site. So absent data means
 * the old behaviour, and parallelism is something a plan EARNS by saying what
 * actually depends on what.
 */
export function planDeclaresNeeds(
  q: Pick<QueueExecutionInput, 'planMembers'>,
): boolean {
  return q.planMembers.some((m) => Array.isArray(m.needs))
}

/**
 * The step numbers one member waits on. In a graph plan that is exactly what
 * it declared (no declaration ⇒ nothing, so it starts immediately); in a
 * ladder plan it is every step below its own — which is the old rule, spelled
 * out. Unnumbered members sort last either way (UNSTEPPED), matching
 * `orderedPlanMembers`.
 */
export function effectiveNeeds(
  q: Pick<QueueExecutionInput, 'planMembers'>,
  m: { step: number | null; needs?: readonly number[] | null },
): number[] {
  if (planDeclaresNeeds(q)) {
    return [...new Set(m.needs ?? [])].sort((a, b) => a - b)
  }
  const mine = stepValueOf(m)
  return [...new Set(q.planMembers.map(stepValueOf).filter((s) => s < mine))]
    .sort((a, b) => a - b)
}

/**
 * The queue's CURSOR: the lowest step that still has unfinished members —
 * i.e. the step a run may work right now.
 *
 * DERIVED, never stored. A materialized cursor would be a second source of
 * truth that drifts the moment a member is resolved, reactivated or declined
 * out of band; computing it from the members' own stages makes "the cursor
 * stays when a run unclaims" true by construction rather than by remembering
 * to write it.
 */
export interface QueueCursor {
  /** The step being worked: its number, or null when the members at this
   *  point carry no step number (an unordered plan, or the unnumbered tail). */
  step: number | null
  /** The members at that step — a run takes ALL of them together, which is
   *  what makes a queue step ONE unit of the run's budget. */
  bugIds: string[]
  /** Nothing left to work: every member is finished or declined. */
  done: boolean
  /** The members a run may take RIGHT NOW — every prerequisite satisfied and
   *  no unanswered question. In a ladder plan this is `bugIds`; in a graph
   *  plan it can span several steps at once, which is the whole point.
   *  Populated by `queueExecutionGate` (which alone knows the answers);
   *  `queueCursor` on its own leaves it empty. */
  runnable: string[]
}

/**
 * `stageOf` returns the member's own stage (bugPipelineStage). A member the
 * caller did not pass returns undefined and counts as UNFINISHED — we cannot
 * prove a bug shipped by failing to look at it.
 *
 * A member the plan DECLINED (`verdict: 'wont-fix'`) never holds a step: the
 * operator's plan already said it will not be built.
 */
export function queueCursor(
  q: Pick<QueueExecutionInput, 'planMembers'>,
  stageOf: (bugId: string) => PipelineStage | undefined,
): QueueCursor {
  const pending = q.planMembers.filter((m) => {
    if (m.verdict === 'wont-fix') return false
    const stage = stageOf(m.bugId)
    return !(stage && QUEUE_FINISHED_STAGES.includes(stage))
  })
  if (pending.length === 0) return { step: null, bugIds: [], done: true, runnable: [] }
  const cursor = Math.min(...pending.map(stepValueOf))
  return {
    step: cursor === UNSTEPPED ? null : cursor,
    bugIds: pending.filter((m) => stepValueOf(m) === cursor).map((m) => m.bugId),
    done: false,
    runnable: [],
  }
}

/**
 * The `after` prerequisites that are NOT finished yet. An id with no queue is
 * counted as UNMET and named: a plan pointing at something that cannot be
 * found is a plan whose order we cannot honour, and waiting visibly is the
 * safe direction (the operator can re-plan; a silent start cannot be undone).
 */
export function unmetPrerequisites(
  q: Pick<QueueExecutionInput, 'after'>,
  byId: ReadonlyMap<string, Pick<QueueExecutionInput, 'id' | 'title' | 'status'>>,
): Array<{ id: string; title: string; status: string }> {
  const unmet: Array<{ id: string; title: string; status: string }> = []
  for (const id of q.after ?? []) {
    const other = byId.get(id)
    if (!other) unmet.push({ id, title: id, status: 'not found' })
    else if (other.status !== 'done') unmet.push({ id, title: other.title, status: other.status })
  }
  return unmet
}

export interface QueueExecutionGate {
  /** bugId → why it may not be worked yet. Absent ⇔ not gated by any plan. */
  waiting: Map<string, WaitingReason>
  /** queueId → its cursor, for the board and the CLI. Executable queues only. */
  cursors: Map<string, QueueCursor>
}

/**
 * WHO the pipeline tells when it needs a human — ONE rule.
 *
 * The reporter leads and the operator is copied, because the person who filed
 * the report usually holds the answer. The reporter is dropped when there is no
 * reporter email, when they ARE the operator (never self-CC), or when the
 * notice was marked operator-only (a sequencing question a reporter cannot
 * answer).
 *
 * Extracted Sep 9 2026 so the BOARD and the CRON cannot disagree. The board now
 * states who was emailed and when; if it re-derived that from its own copy of
 * this rule it would eventually claim someone was asked who never was — which
 * is precisely the failure it exists to surface (Oliver was never told about
 * the two questions waiting on him).
 */
export interface PipelineAudience {
  /** Lead recipient. */
  to: string
  /** Copied, in order, never containing `to`. Empty ⇔ nobody else. */
  cc: string[]
  /** True ⇔ the reporter leads. */
  asksReporter: boolean
  /** The area owner that decided this, if any — what a card names. */
  ownerKey: string | null
}

/**
 * WHO gets asked.
 *
 * 1. The AREA OWNER leads when the surface has one (operator, Sep 9 2026:
 *    "send questions to the people involved … all sales related bugs and
 *    features go there"). A decision about the sales cockpit belongs to whoever
 *    runs sales, no matter who happened to file it — which is the case that
 *    exposed this: the operator filed it, so it came back to the operator.
 * 2. Otherwise the REPORTER leads, as before.
 * 3. The operator is ALWAYS copied, and never CC'd to themselves.
 *
 * The reporter is copied when someone else leads: they filed it and should see
 * where it went.
 */
export function pipelineAskAudience(args: {
  reporterEmail?: string | null
  operatorEmail: string
  /** Should the reporter lead? A notice can be marked operator-only. */
  wantsReporter: boolean
  /** The area's owner, from resolveAreaOwner. Absent ⇒ rule 2. */
  owner?: { key: string; to: string; cc: readonly string[] } | null
}): PipelineAudience {
  const operator = args.operatorEmail.trim()
  const reporter = (args.reporterEmail ?? '').trim()
  const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()

  // Deduped, order-preserving, and never the lead — a CC that repeats the TO
  // is how one person gets two copies of the same question.
  const ccOf = (to: string, wanted: readonly (string | null | undefined)[]): string[] => {
    const out: string[] = []
    for (const raw of wanted) {
      const v = (raw ?? '').trim()
      if (!v || same(v, to) || out.some((o) => same(o, v))) continue
      out.push(v)
    }
    return out
  }

  if (args.owner?.to) {
    return {
      to: args.owner.to,
      cc: ccOf(args.owner.to, [...args.owner.cc, reporter, operator]),
      asksReporter: !!reporter && same(args.owner.to, reporter),
      ownerKey: args.owner.key,
    }
  }
  const asksReporter = args.wantsReporter && !!reporter && !same(reporter, operator)
  return asksReporter
    ? { to: reporter, cc: ccOf(reporter, [operator]), asksReporter: true, ownerKey: null }
    : { to: operator, cc: [], asksReporter: false, ownerKey: null }
}

/**
 * The members whose plan is stopped on a question nobody has answered, and who
 * have not been asked yet.
 *
 * Operator ask, Sep 9 2026: "if it does need a response from someone, the pipe
 * needs to send an email to me and Oliver and request input." The plan email
 * already carried the questions but went to the OPERATOR ALONE — so the person
 * who filed the report, and who usually holds the answer, was never told. Its
 * own stamp (not the plan's `emailedAt`) so a plan already emailed still chases
 * an unanswered question, and so it is asked exactly once.
 */
export function selectQuestionAsks(
  rows: ReadonlyArray<{ id: string; waitingKind: WaitingKind | null }>,
  askedAlready: (bugId: string) => boolean,
): string[] {
  return rows
    .filter((r) => r.waitingKind === 'needs-answer' && !askedAlready(r.id))
    .map((r) => r.id)
}

/** A member whose plan asked a question ABOUT IT and got no answer. The plan
 *  wrote `needs-decision` against this member deliberately — it is not a
 *  confidence score, it is "I cannot choose this for you" — so the pipeline
 *  must not pick one itself just because the member's turn arrived. */
export function memberNeedsAnswer(
  m: { verdict?: string | null },
  answered: boolean,
): boolean {
  return m.verdict === 'needs-decision' && !answered
}

/**
 * Gate every member of every executable plan. A member may be worked when
 * every plan in `after` is done, every step in its own `effectiveNeeds` is
 * finished, and no unanswered question stands against it. Pure; the caller
 * supplies the members' stages and who has answered.
 *
 * Three waiting kinds come out, never one blurred string, because they are
 * three different asks of the reader: `other-plan` and `earlier-step` say
 * WAIT, `needs-answer` says ACT. The board lanes and the notify cron both
 * read the kind, so a question can never sit in a lane labelled as though it
 * were a scheduling delay — which is exactly how two of them went unnoticed.
 *
 * Not gated, deliberately: any queue that is not an approved fix-in-order
 * plan, and any member whose own stage is not `fix` (see `isGateable` — an
 * exhausted or blocked bug needs a human whatever the plan says).
 */
export function queueExecutionGate(
  queues: readonly QueueExecutionInput[],
  stageOf: (bugId: string) => PipelineStage | undefined,
  /** Has the operator answered the plan's question about this member? Omitted
   *  ⇒ NOBODY has — the safe direction, because guessing an answer the plan
   *  explicitly refused to guess is the one outcome nothing downstream can
   *  undo (a published privacy notice, a retired public URL). */
  answeredOf: (bugId: string) => boolean = () => false,
): QueueExecutionGate {
  const byId = new Map(queues.map((q) => [q.id, q]))
  const waiting = new Map<string, WaitingReason>()
  const cursors = new Map<string, QueueCursor>()
  for (const q of queues) {
    if (!isExecutableQueue(q)) continue
    const cursor = queueCursor(q, stageOf)
    const unmet = unmetPrerequisites(q, byId)
    const blockedByPlan: WaitingReason | null = unmet.length
      ? {
        kind: 'other-plan',
        text: `Waiting on another plan first: ${unmet.map((u) => `${u.title} (${u.status})`).join(', ')}.`,
      }
      : null

    /** A step is SATISFIED when every member sitting on it is finished or
     *  declined. Unfinished members of a needed step are named, so the reason
     *  points at the thing to chase rather than at a number. */
    const holdersOf = (step: number): string | null => {
      const ids = q.planMembers
        .filter((m) => stepValueOf(m) === step && m.verdict !== 'wont-fix')
        .filter((m) => {
          const s = stageOf(m.bugId)
          return !(s && QUEUE_FINISHED_STAGES.includes(s))
        })
        .map((m) => m.bugId)
      if (!ids.length) return null
      const label = step === UNSTEPPED ? 'the unnumbered members' : `step ${step}`
      return `${label} (${ids.join(', ')})`
    }

    const runnable: string[] = []
    for (const m of q.planMembers) {
      if (blockedByPlan) { waiting.set(m.bugId, blockedByPlan); continue }
      const stage = stageOf(m.bugId)
      const finished = Boolean(stage && QUEUE_FINISHED_STAGES.includes(stage))
      if (finished || m.verdict === 'wont-fix') continue

      const blockers = effectiveNeeds(q, m)
        .map(holdersOf)
        .filter((s): s is string => s !== null)
      if (blockers.length) {
        const mine = m.step === null
          ? 'it has no step number, so it runs last'
          : `this is step ${m.step}`
        waiting.set(m.bugId, {
          kind: 'earlier-step',
          text: `Waiting its turn in ${q.title}: ${blockers.join(', ')} is not finished — ${mine}.`,
        })
        continue
      }
      // Its turn HAS come. The only thing that can still hold it is a question
      // the plan asked about it — and that is a person's to clear, not a step's.
      if (memberNeedsAnswer(m, answeredOf(m.bugId))) {
        waiting.set(m.bugId, {
          kind: 'needs-answer',
          text: `Ready to build, but ${q.title} asked a question about this one and nobody has answered it yet.`,
        })
        continue
      }
      runnable.push(m.bugId)
    }
    cursors.set(q.id, { ...cursor, runnable })
  }
  return { waiting, cursors }
}

// ── Queue view (ONE definition — the CLI `list`, the /settings/bugs/pipeline
//    board, and anything else that renders the queue all call this, so no two
//    surfaces can ever disagree about a bug's stage or the work order) ───────

/** Stages the pipeline will actually act on, in the order work is taken. */
export const ACTIONABLE_STAGES: readonly PipelineStage[] = ['fix', 'verify', 'recommend', 'exhausted']

const PRIORITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 }

export type PipelineQueueInput = PipelineBugView & Pick<BugReport,
  'id' | 'title' | 'severity' | 'priority' | 'createdAt' | 'updatedAt'
  | 'pipelineFixCommit' | 'fixRecommendationConfidence' | 'fixRecommendationVerdict' | 'resolveEmailedAt'
  | 'pipelineOperatorGuidance' | 'pipelineOperatorGuidanceAt' | 'pipelineWorkLog' | 'pipelineQueueId'
  | 'resolutionKind' | 'duplicateOf'>
  // Who was told, and whether they were: the row states it, so a reader never has
  // to assume the reporter has seen a question addressed to them.
  & Partial<Pick<BugReport,
    'reporterEmail' | 'reporterName' | 'pipelineQuestionEmailedAt'
    | 'pipelineNoticeEmailedAt' | 'pipelineNoticeAsksReporter' | 'recommendationEmailedAt'
    // Where the bug was filed + its area pick — how an AREA OWNER is resolved.
    | 'currentRoute' | 'currentUrl' | 'affectedArea'>>

export interface PipelineQueueRow {
  id: string
  stage: PipelineStage
  /** True while a live tick holds the claim and no fix is pushed yet — the
   *  "being worked right now" display fact, computed HERE so no renderer
   *  re-inlines the assignee marker or claim logic. */
  activeClaim: boolean
  title: string
  project: BugReport['project']
  severity: BugReport['severity']
  priority: BugReport['priority']
  status: BugReport['status']
  approvalStatus: BugReport['approvalStatus']
  assignee: string | null
  pipelineAttempts: number
  pipelineClaimedAt: string | null
  pipelineFixCommit: string | null
  pipelineFixPushedAt: string | null
  fixRecommendationAt: string | null
  fixRecommendationConfidence: BugReport['fixRecommendationConfidence'] | null
  /** The tick's verdict — the board badges it so a "won't fix" or a
   *  counter-proposal is visible before the operator taps Approve. */
  fixRecommendationVerdict: BugReport['fixRecommendationVerdict'] | null
  recommendationEmailedAt: string | null
  resolveEmailedAt: string | null
  pipelineBlockedReason: string | null
  pipelineOperatorGuidance: string | null
  pipelineOperatorGuidanceAt: string | null
  /** A [CLAUDE feature work] item — built in slices, one per run. Computed
   *  HERE from the tag so no renderer re-inlines the title test. */
  featureWork: boolean
  /** The item's slice log — its cursor; null on bugs and on unstarted items. */
  pipelineWorkLog: string | null
  /** The intake queue this bug belongs to (work item A) — the DOC's membership
   *  when the caller passed queue summaries (the doc is the truth), else the
   *  row's own `pipelineQueueId` mirror. Null ⇔ not in a queue. */
  queueId: string | null
  /** Item D: why an approved member of a fix-in-order plan may not be worked
   *  yet (which plan / which step). Set ⇔ stage is 'waiting'; the board lane
   *  and the CLI print THIS string, so no surface re-derives the reason. */
  waitingReason: string | null
  /** WHY it waits — the board lanes split on this and the notify cron emails
   *  on `needs-answer`. Null when `waitingReason` came from the consolidation
   *  hold rather than the plan gate. */
  waitingKind: WaitingKind | null
  /**
   * What the pipeline last SENT about this bug, and to whom. Null ⇔ nothing has
   * been emailed yet.
   *
   * Operator, Sep 9 2026: "you should state if email was sent and when and to
   * whom — in this case Oliver was not notified." A card that shows a question
   * without saying whether anyone was told reads as though the reporter is
   * sitting on it, when in fact nobody ever asked them.
   */
  notification: { kind: 'question' | 'notice' | 'recommendation'; at: string; to: string[] } | null
  /** TRUE ⇔ this bug needs a human and NOBODY has been emailed yet. The one
   *  state the board must never render as a neutral wait. */
  notifyPending: boolean
  /** Item C: where this report went — the shared display fact (consolidationLink), so the board
   *  prints the same chip the roster and the bug page do. Null on everything else. */
  consolidation: ConsolidationLink | null
  createdAt: string
  updatedAt: string
}

export interface PipelineQueueView {
  counts: Record<string, number>
  rows: PipelineQueueRow[]
  /** Item D: queueId → the step each executable plan is on, for the board's
   *  waiting lane and the CLI's list. Empty when the caller passed no queues. */
  queueCursors: Record<string, QueueCursor>
  /** Intake-queue summaries (work item A) — EXACTLY what the caller passed
   *  (produced by lib/bug-queues queueSummaries; the CLI `list` and the board
   *  strip render THIS array, never their own reduction). Empty when the
   *  caller had no queue doc. */
  queues: QueueSummary[]
}

/** ONLY a bug waiting to be BUILT is gated. A pushed fix still needs its
 *  verification, a recommendation is analysis rather than a build, and an
 *  exhausted/blocked bug needs a human either way — holding any of those
 *  behind a plan's build order would strand real work. */
const isGateable = (stage: PipelineStage | undefined): boolean => stage === 'fix'

const gateStage = (stage: PipelineStage, reason: WaitingReason | undefined): PipelineStage =>
  (reason && isGateable(stage)) ? 'waiting' : stage

/** The stages where a human is genuinely being waited on. Only these can be
 *  `notifyPending` — a bug the pipeline is happily building owes nobody mail. */
const NEEDS_A_HUMAN: readonly PipelineStage[] = ['blocked', 'exhausted', 'await-approval']

/**
 * What was sent about this bug, to whom, and whether anyone was told at all.
 *
 * The recipients are NOT re-derived here: `pipelineAskAudience` is the one rule
 * the cron sends by, so the card states what actually happened rather than what
 * a second copy of the rule would have done.
 */
function notificationFacts(
  b: PipelineQueueInput,
  stage: PipelineStage,
  waitingKind: WaitingKind | null,
  operatorEmail: string | null,
): Pick<PipelineQueueRow, 'notification' | 'notifyPending'> {
  const operator = (operatorEmail ?? '').trim()
  const named = (wantsReporter: boolean): string[] => {
    // With no operator address in hand the row still says WHETHER mail went and
    // to which ROLE — a generic label is honest; a guessed address is not.
    if (!operator) return wantsReporter && (b.reporterEmail ?? '').trim() ? ['the reporter', 'the operator'] : ['the operator']
    // The SAME rule the send uses, area owner included — so the card names the
    // people who actually received it, in the order they appear on the email.
    const a = pipelineAskAudience({
      reporterEmail: b.reporterEmail,
      operatorEmail: operator,
      wantsReporter,
      owner: resolveAreaOwner({ currentRoute: b.currentRoute, currentUrl: b.currentUrl, area: b.affectedArea }),
    })
    return [a.to, ...a.cc]
  }

  // Newest first: the most recent thing said about this bug is what a reader
  // needs, and an older stamp under a newer one reads as "nobody was told".
  const candidates: Array<{ kind: 'question' | 'notice' | 'recommendation'; at?: string | null; to: string[] }> = [
    { kind: 'question', at: b.pipelineQuestionEmailedAt, to: named(true) },
    { kind: 'notice', at: b.pipelineNoticeEmailedAt, to: named(b.pipelineNoticeAsksReporter !== false) },
    { kind: 'recommendation', at: b.recommendationEmailedAt, to: named(false) },
  ]
  const sent = candidates
    .filter((c): c is { kind: 'question' | 'notice' | 'recommendation'; at: string; to: string[] } =>
      typeof c.at === 'string' && !!c.at)
    .sort((x, y) => y.at.localeCompare(x.at))[0] ?? null

  const needsHuman = NEEDS_A_HUMAN.includes(stage) || waitingKind === 'needs-answer'
  return {
    notification: sent ? { kind: sent.kind, at: sent.at, to: sent.to } : null,
    notifyPending: needsHuman && !sent,
  }
}

export function annotatePipelineQueue(
  items: PipelineQueueInput[],
  now: Date,
  queues?: readonly QueueSummary[],
  /** The operator's address, so a row can NAME who was emailed. Passed in
   *  rather than imported: SUPER_ADMIN_EMAIL lives in lib/acl, which imports
   *  KV, and this module is bundled by the board. Absent ⇒ recipients are
   *  described generically ("the operator") rather than guessed. */
  opts?: { operatorEmail?: string | null },
): PipelineQueueView {
  // Doc membership beats the row mirror: the mirror is a cache written after
  // the doc (and self-healing), so when the summaries are in hand they decide.
  const memberOf = new Map<string, string>()
  for (const q of queues ?? []) for (const bugId of q.memberIds) memberOf.set(bugId, q.id)
  const stageRank = (s: PipelineStage) => {
    const i = ACTIONABLE_STAGES.indexOf(s)
    return i === -1 ? 99 : i
  }
  // Item D: a member of an approved fix-in-order plan is claimable only on
  // its turn. Computed from the SAME stages the rows carry, so the gate can
  // never disagree with the queue it is gating.
  const ownStage = new Map<string, PipelineStage>()
  for (const b of items) ownStage.set(b.id, bugPipelineStage(b, now))
  // An answer is the operator's guidance arriving AFTER the plan asked — a note
  // typed before the question was written cannot be its answer.
  const answeredAt = new Map<string, string>()
  for (const b of items) {
    if (b.pipelineOperatorGuidance && b.pipelineOperatorGuidanceAt) {
      answeredAt.set(b.id, b.pipelineOperatorGuidanceAt)
    }
  }
  const gate = queueExecutionGate(
    queues ?? [],
    (id) => ownStage.get(id),
    (id) => answeredAt.has(id),
  )
  const rows: PipelineQueueRow[] = items.map(b => ({
    id: b.id,
    stage: gateStage(ownStage.get(b.id) ?? bugPipelineStage(b, now), gate.waiting.get(b.id)),
    activeClaim: b.status === 'in_progress'
      && (b.assignee ?? '') === PIPELINE_ASSIGNEE
      && !b.pipelineFixPushedAt,
    title: b.title,
    project: b.project,
    severity: b.severity,
    priority: b.priority,
    status: b.status,
    approvalStatus: b.approvalStatus,
    assignee: b.assignee ?? null,
    pipelineAttempts: b.pipelineAttempts ?? 0,
    pipelineClaimedAt: b.pipelineClaimedAt ?? null,
    pipelineFixCommit: b.pipelineFixCommit ?? null,
    pipelineFixPushedAt: b.pipelineFixPushedAt ?? null,
    fixRecommendationAt: b.fixRecommendationAt ?? null,
    fixRecommendationConfidence: b.fixRecommendationConfidence ?? null,
    fixRecommendationVerdict: b.fixRecommendationVerdict ?? null,
    recommendationEmailedAt: b.recommendationEmailedAt ?? null,
    resolveEmailedAt: b.resolveEmailedAt ?? null,
    pipelineBlockedReason: b.pipelineBlockedReason ?? null,
    pipelineOperatorGuidance: b.pipelineOperatorGuidance ?? null,
    pipelineOperatorGuidanceAt: b.pipelineOperatorGuidanceAt ?? null,
    featureWork: isFeatureWorkItem(b),
    pipelineWorkLog: b.pipelineWorkLog ?? null,
    queueId: (queues ? memberOf.get(b.id) : b.pipelineQueueId) ?? null,
    // The plan gate speaks first (an approved member waiting its turn), then the consolidation
    // hold — two reasons a row can sit in the waiting lane, ONE string each, never a blank card.
    waitingReason: ((gate.waiting.has(b.id) && isGateable(ownStage.get(b.id)))
      ? gate.waiting.get(b.id)!.text : null) ?? consolidationHold(b),
    waitingKind: (gate.waiting.has(b.id) && isGateable(ownStage.get(b.id)))
      ? gate.waiting.get(b.id)!.kind : null,
    ...notificationFacts(
      b,
      gateStage(ownStage.get(b.id) ?? bugPipelineStage(b, now), gate.waiting.get(b.id)),
      (gate.waiting.has(b.id) && isGateable(ownStage.get(b.id)))
        ? gate.waiting.get(b.id)!.kind : null,
      opts?.operatorEmail ?? null,
    ),
    consolidation: consolidationLink(b),
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
  }))
  rows.sort((a, b) =>
    (stageRank(a.stage) - stageRank(b.stage))
    || ((PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9))
    || a.createdAt.localeCompare(b.createdAt))
  const counts: Record<string, number> = {}
  for (const r of rows) counts[r.stage] = (counts[r.stage] ?? 0) + 1
  return {
    counts,
    rows,
    queueCursors: Object.fromEntries(gate.cursors),
    queues: [...(queues ?? [])],
  }
}

// ── Model routing (item D, slice 3): what sizes THIS run's brain ─────────────

/**
 * The work a run could actually START right now.
 *
 * Stage `fix` is exactly that set: approved, not blocked, inside the attempt
 * cap, not held by a live claim — and, since item D, not `waiting` its turn in
 * an approved plan. `verify` and `recommend` are real work too, but they are
 * not what the run's model is sized from: verification is a poll and a
 * recommendation is analysis, while the fix stage is the one that writes code.
 *
 * Takes ANNOTATED rows (annotatePipelineQueue), never raw bugs, so the gate
 * that made a member `waiting` is the same one every surface renders — a
 * caller that re-derived "approved and not started" from the raw row would be
 * a second, queue-blind opinion about what may be worked.
 */
export function claimableWork<T extends { stage: PipelineStage }>(rows: readonly T[]): T[] {
  return rows.filter((r) => r.stage === 'fix')
}

/**
 * The priority the run's MODEL follows: the top priority among CLAIMABLE work
 * (the fix stage takes the highest-priority bug first, so per-run == per-bug
 * for the implementation work). Null ⇔ nothing is claimable — a
 * recommendation-only or idle tick, which the caller maps to its default.
 *
 * Order-independent by construction (it sorts by PRIORITY_ORDER rather than
 * trusting the caller's array order), and an unknown priority word sorts last
 * instead of throwing: a routing helper must never be the thing that stops a
 * tick.
 */
export function topClaimablePriority(
  rows: readonly Pick<PipelineQueueRow, 'stage' | 'priority'>[],
): PipelineQueueRow['priority'] | null {
  const claimable = claimableWork(rows)
  if (claimable.length === 0) return null
  return [...claimable]
    .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9))[0]
    .priority
}

// ── The run's BATCH — how many DEPLOYS the queue costs (BUG-mulyeb9z-ddl8) ────

/**
 * How many bugs one run may implement and ship in ONE push.
 *
 * Operator directive Aug 31 2026 allowed three, and TICK-CLOUD.md has said so
 * ever since — but it said it in prose, to a run that had to work out for
 * itself which three. Measured over Sep 8–29 2026: 48 pushes for 41 bugs, 1.04
 * bugs per deploy, the allowance exercised TWICE. Every push is a Vercel build
 * (~6.3 min billed), so the queue was costing ~5 h of build a month to ship
 * work that fits in a fifth of that.
 *
 * `planFixBatch` below turns the prose into a computed, printed fact, so a run
 * reads its batch instead of deciding one.
 */
export const MAX_FIXES_PER_RUN = 3

/**
 * The two brains a run can have. The PREFILTER owns the priority→model map
 * (it is the step that routes before the agent exists); this is the same split
 * named as a TIER, so the batch can say "same brain as the lead" without a
 * second copy of the model ids.
 */
export type ModelTier = 'fable' | 'opus'

export function modelTierOf(priority: BugReport['priority']): ModelTier {
  return priority === 'critical' || priority === 'high' ? 'fable' : 'opus'
}

/** A claimable bug this run may NOT take, and the one-line reason. Printed, not
 *  dropped: a batch of one is fine, a batch of one for no stated reason is how
 *  "batching is on" and "nothing ever batches" coexisted for a month. */
export interface HeldBackWork {
  id: string
  why: string
}

export interface FixBatch {
  /** The bugs to implement this run, in the order to implement them. May be
   *  empty (nothing claimable — a recommend-only or verify-only tick). */
  bugIds: string[]
  /** The tier the batch rides (the lead's), null when the batch is empty. */
  tier: ModelTier | null
  /** The ONE repo this run touches — `website` fixes live in another repo and
   *  are never batched with portal work. */
  project: BugReport['project'] | null
  /** Why the batch stops where it does, for the run log. */
  cap: number
  heldBack: HeldBackWork[]
}

/** What the batch reads off an annotated row. Nothing else: the stage already
 *  carries the approval, the attempt cap, the live claim and the plan gate. */
export type FixBatchRow = Pick<PipelineQueueRow,
  'id' | 'stage' | 'priority' | 'project' | 'featureWork' | 'queueId' | 'createdAt'>

/**
 * THE BATCH THIS RUN SHOULD TAKE — every claimable bug the run may ship in one
 * push, in build order, capped at `MAX_FIXES_PER_RUN`.
 *
 * Takes ANNOTATED rows (annotatePipelineQueue), never raw bugs, so the plan
 * gate, the attempt cap and live claims have already spoken — this function
 * adds only the rails TICK-CLOUD.md states in prose, and adds them once:
 *
 *   - the LEAD is the highest-priority claimable bug (ties oldest-first, so it
 *     is order-independent and two runs reading one queue agree),
 *   - BUGS BEFORE FEATURE WORK — a `[CLAUDE feature work]` item is taken only
 *     when no ordinary bug is claimable, and then it is the WHOLE run,
 *   - ONE REPO PER RUN — a `website` fix is the whole run (its own clone, gate,
 *     push and deploy wait do not fit beside a portal fix),
 *   - SAME TIER as the lead for standalone bugs (never mix the brains), EXCEPT
 *     members of the lead's own plan step, which travel together across tiers,
 *   - at most `MAX_FIXES_PER_RUN` implementations.
 *
 * Pure and non-mutating: rows in, ids out.
 */
export function planFixBatch(rows: readonly FixBatchRow[]): FixBatch {
  const ordered = [...claimableWork(rows)].sort((a, b) =>
    ((PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9))
    || a.createdAt.localeCompare(b.createdAt)
    || a.id.localeCompare(b.id))
  const empty: FixBatch = { bugIds: [], tier: null, project: null, cap: MAX_FIXES_PER_RUN, heldBack: [] }
  if (ordered.length === 0) return empty

  // BUGS FIRST. A feature slice fills the gaps between bugs; it never takes a
  // run a bug could have had (TICK-CLOUD.md, Sep 5 2026 — a bundle sat approved
  // for five ticks while feature slices took every run).
  const lead = ordered.find(r => !r.featureWork) ?? ordered[0]
  const soloReason = lead.featureWork
    ? 'a feature-work slice is the whole run'
    : (lead.project === 'website' ? 'a website fix is the whole run — its code is in another repo' : null)

  const bugIds = [lead.id]
  const heldBack: HeldBackWork[] = []
  for (const r of ordered) {
    if (r.id === lead.id) continue
    const why = soloReason
      ?? (r.featureWork ? 'feature work waits for a run with no claimable bug'
        : r.project !== lead.project ? `another repo (${r.project}) — one repo per run`
        : (modelTierOf(r.priority) !== modelTierOf(lead.priority)
            && !(r.queueId && r.queueId === lead.queueId))
          ? `${modelTierOf(r.priority)}-tier work — a run never mixes the two brains`
        : bugIds.length >= MAX_FIXES_PER_RUN ? `over the ${MAX_FIXES_PER_RUN}-per-run cap`
        : null)
    if (why) heldBack.push({ id: r.id, why })
    else bugIds.push(r.id)
  }
  return {
    bugIds,
    tier: modelTierOf(lead.priority),
    project: lead.project,
    cap: MAX_FIXES_PER_RUN,
    heldBack,
  }
}
